import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Tabs, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Chip, Button, TextField, IconButton, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
} from '@mui/material';
import { SupportAgent, School, Add, Delete, Code } from '@mui/icons-material';
import { api } from '../config/api';

const estadoColor: Record<string, any> = { abierto: 'error', en_proceso: 'warning', cerrado: 'success' };

const Soporte: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [tickets, setTickets] = useState<any[]>([]);
  const [resumen, setResumen] = useState<any>({});
  const [correcciones, setCorrecciones] = useState<any[]>([]);
  const [widget, setWidget] = useState<any>(null);
  const [snippet, setSnippet] = useState('');
  const [loading, setLoading] = useState(true);
  const [dlgEnt, setDlgEnt] = useState(false);
  const [nuevaCorr, setNuevaCorr] = useState({ pregunta: '', respuesta_buena: '' });
  const [msg, setMsg] = useState('');

  const cargar = () => {
    setLoading(true);
    Promise.all([
      api.get('/soporte/tickets'),
      api.get('/soporte/entrenamiento'),
      api.get('/widget'),
    ]).then(([t, e, w]) => {
      setTickets(t.data.tickets || []); setResumen(t.data.resumen || {});
      setCorrecciones(e.data.correcciones || []);
      setWidget(w.data.config); setSnippet(w.data.snippet || '');
    }).finally(() => setLoading(false));
  };
  useEffect(cargar, []); // eslint-disable-line

  const cambiarEstadoTicket = async (id: number, estado: string) => {
    await api.put(`/soporte/tickets/${id}`, { estado });
    cargar();
  };

  const guardarCorreccion = async () => {
    await api.post('/soporte/entrenamiento', nuevaCorr);
    setDlgEnt(false); setNuevaCorr({ pregunta: '', respuesta_buena: '' }); cargar();
  };
  const borrarCorreccion = async (id: number) => { await api.delete(`/soporte/entrenamiento/${id}`); cargar(); };

  const guardarWidget = async () => {
    await api.put('/widget', widget); setMsg('Widget guardado'); cargar();
  };
  const copiarSnippet = () => { navigator.clipboard.writeText(snippet); setMsg('Snippet copiado'); };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SupportAgent color="primary" />
        <Typography variant="h4">Soporte y Entrenamiento</Typography>
      </Stack>
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<SupportAgent />} iconPosition="start" label={`Tickets (${resumen.abiertos || 0} abiertos)`} />
        <Tab icon={<School />} iconPosition="start" label="Entrenamiento" />
        <Tab icon={<Code />} iconPosition="start" label="Widget web" />
      </Tabs>

      {tab === 0 && (
        <Card><CardContent>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip label={`Abiertos: ${resumen.abiertos || 0}`} color="error" />
            <Chip label={`En proceso: ${resumen.en_proceso || 0}`} color="warning" />
            <Chip label={`Cerrados: ${resumen.cerrados || 0}`} color="success" />
          </Stack>
          <TableContainer>
            <Table>
              <TableHead><TableRow><TableCell>Asunto</TableCell><TableCell>Contacto</TableCell><TableCell>Estado</TableCell><TableCell>Acción</TableCell></TableRow></TableHead>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.asunto}</TableCell>
                    <TableCell>{t.nombre_contacto || t.telefono}</TableCell>
                    <TableCell><Chip size="small" label={t.estado} color={estadoColor[t.estado]} /></TableCell>
                    <TableCell>
                      <TextField select size="small" value={t.estado} onChange={(e) => cambiarEstadoTicket(t.id, e.target.value)} sx={{ width: 140 }}>
                        <MenuItem value="abierto">Abierto</MenuItem>
                        <MenuItem value="en_proceso">En proceso</MenuItem>
                        <MenuItem value="cerrado">Cerrado</MenuItem>
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
                {tickets.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin tickets</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent></Card>
      )}

      {tab === 1 && (
        <Card><CardContent>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Corrige respuestas para que tu agente aprenda. Estas se usan como ejemplos en cada respuesta.</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setDlgEnt(true)}>Agregar</Button>
          </Stack>
          <Stack spacing={1}>
            {correcciones.map((c) => (
              <Card key={c.id} variant="outlined"><CardContent sx={{ py: '12px !important' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    {c.pregunta && <Typography variant="caption" color="text.secondary">Pregunta: {c.pregunta}</Typography>}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>✅ {c.respuesta_buena}</Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => borrarCorreccion(c.id)}><Delete fontSize="small" /></IconButton>
                </Stack>
              </CardContent></Card>
            ))}
            {correcciones.length === 0 && <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>Aún no hay correcciones</Typography>}
          </Stack>
        </CardContent></Card>
      )}

      {tab === 2 && widget && (
        <Card><CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Chat para tu sitio web</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pega este código antes de &lt;/body&gt; en tu web y aparecerá el chat conectado a tu agente.
          </Typography>
          <TextField fullWidth multiline value={snippet} InputProps={{ readOnly: true }} sx={{ mb: 1, fontFamily: 'monospace' }} />
          <Button size="small" startIcon={<Code />} onClick={copiarSnippet} sx={{ mb: 3 }}>Copiar código</Button>
          <Typography variant="h6" sx={{ mb: 2 }}>Personalización</Typography>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <TextField label="Título" fullWidth value={widget.titulo || ''} onChange={(e) => setWidget({ ...widget, titulo: e.target.value })} />
            <TextField label="Subtítulo" fullWidth value={widget.subtitulo || ''} onChange={(e) => setWidget({ ...widget, subtitulo: e.target.value })} />
            <TextField label="Mensaje inicial" fullWidth value={widget.mensaje_inicial || ''} onChange={(e) => setWidget({ ...widget, mensaje_inicial: e.target.value })} />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="Color" type="color" value={widget.color || '#25D366'} onChange={(e) => setWidget({ ...widget, color: e.target.value })} sx={{ width: 100 }} />
              <TextField select label="Posición" value={widget.posicion || 'right'} onChange={(e) => setWidget({ ...widget, posicion: e.target.value })} sx={{ width: 160 }}>
                <MenuItem value="right">Derecha</MenuItem>
                <MenuItem value="left">Izquierda</MenuItem>
              </TextField>
            </Stack>
            <Button variant="contained" onClick={guardarWidget} sx={{ alignSelf: 'flex-start' }}>Guardar widget</Button>
          </Stack>
        </CardContent></Card>
      )}

      <Dialog open={dlgEnt} onClose={() => setDlgEnt(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva corrección</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Pregunta del cliente (opcional)" fullWidth value={nuevaCorr.pregunta} onChange={(e) => setNuevaCorr({ ...nuevaCorr, pregunta: e.target.value })} />
            <TextField label="Respuesta correcta que debe dar" fullWidth multiline rows={3} value={nuevaCorr.respuesta_buena} onChange={(e) => setNuevaCorr({ ...nuevaCorr, respuesta_buena: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgEnt(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!nuevaCorr.respuesta_buena} onClick={guardarCorreccion}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Soporte;
